import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Zap, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { payments } from "@/lib/payments/adapter";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideaId: string;
  ideaTitle: string;
  onSuccess?: () => void;
}

export default function BoostIdeaDialog({ open, onOpenChange, ideaId, ideaTitle, onSuccess }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: packages } = useQuery({
    queryKey: ["boost-packages"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("boost_packages")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as any[];
    },
  });

  const handleConfirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await payments.startBoostCheckout(ideaId, selected);
      toast.success("Boost activated", { description: "Your idea is now featured." });
      onOpenChange(false);
      setSelected(null);
      onSuccess?.();
    } catch (e: any) {
      toast.error("Boost failed", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Zap className="h-5 w-5 text-amber-400" /> Boost this idea
          </DialogTitle>
          <DialogDescription>
            Surface "{ideaTitle}" in featured discovery lanes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {packages?.map((p) => {
            const isSel = selected === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelected(p.id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  isSel ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-foreground flex items-center gap-2">
                      {p.name}
                      {isSel && <Check className="h-4 w-4 text-primary" />}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-foreground">
                      ${(p.price_cents / 100).toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.duration_days}d</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selected || busy}>
            {busy ? "Processing…" : "Confirm boost"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center">
          Test mode — no real payment is processed.
        </p>
      </DialogContent>
    </Dialog>
  );
}